import { capitalize } from '@leverege/string-util';
import { Attributes } from '@leverege/ui-attributes';
import { useMemo } from 'react';
const INCOMPATIBLE_VALUE_TYPES = ['string', 'boolean', 'enum'];
export default function usePieData(data, {
  isAgg,
  query,
  attribute,
  valueRanges,
  limit,
  includeNull,
  pie,
  donutCount,
  sortValues = true
}) {
  return useMemo(() => {
    if (isAgg) {
      const counts = data.reduce((acc, cur) => {
        const name = cur.data?.key;
        if (!acc[name]) {
          acc[name] = cur.data?.value;
          return acc;
        }
        if (cur.data?.value) {
          acc[name] += cur.data.value;
          return acc;
        }
        if (!cur.data?.value) {
          acc.undefined = (acc.undefined || 0) + 1;
        }
        return acc;
      }, {});
      const total = data.reduce((acc, cur) => {
        if (cur.data?.value) {
          return acc + cur.data.value;
        }
        return acc;
      }, 0);
      const nData = Object.entries(counts).map(([name, value]) => {
        const zeroTotalPercent = value > 0 ? 100 : 0;
        return {
          name,
          value,
          percent: total > 0 ? value / total * 100 : zeroTotalPercent,
          total
        };
      });
      if (sortValues && !query?.ranges?.length) {
        nData.sort((a, b) => b.value - a.value);
      }
      if (donutCount) return donutCount === 'total' ? total : nData?.length;
      return nData;
    }
    const {
      attrName,
      objectType
    } = attribute;
    const valueAttr = Attributes.getAttribute(`${attrName}`, objectType);
    let counts = {};
    if (valueRanges && !INCOMPATIBLE_VALUE_TYPES.includes(valueAttr.valueType)) {
      for (let i = 0; i < valueRanges.length; i++) {
        if (!valueRanges[i].min && !valueRanges[i].max) {
          continue;
        }
        const rangeCount = data.reduce((acc, cur) => {
          const value = valueAttr.get(cur.data)?.value || valueAttr.get(cur.data);
          if (!Number.isNaN(value) && value >= valueRanges[i].min && value <= valueRanges[i].max) {
            acc++;
            return acc;
          }
          return acc;
        }, 0);
        if (pie && rangeCount > 0) {
          counts[valueRanges[i].name] = rangeCount;
        } else {
          counts[valueRanges[i].name] = rangeCount;
        }
      }
    } else {
      counts = data.reduce((acc, cur) => {
        let key = valueAttr.get(cur.data)?.value || valueAttr.get(cur.data);
        if (valueAttr.valueType === 'boolean') key = capitalize(`${valueAttr.get(cur.data)}`);
        if (!acc[key]) {
          acc[key] = 1;
          return acc;
        }
        acc[key] += 1;
        return acc;
      }, {});
    }
    if (valueAttr.valueType === 'boolean') {
      if (counts.Undefined) {
        if (includeNull) counts['No Data'] = counts.Undefined;
        delete counts.Undefined;
      }
      if (counts.Null) {
        if (includeNull) counts['No Data'] = counts.Null + (counts.Undefined || 0);
        delete counts.Null;
      }
    }
    if (counts.undefined) {
      if (includeNull) counts['No Data'] = counts.undefined;
      delete counts.undefined;
    }
    if (counts.null) {
      if (includeNull) counts['No Data'] = counts.null + (counts.undefined || 0);
      delete counts.null;
    }
    const total = Object.values(counts).reduce((a, b) => {
      return a + b;
    }, 0);
    const countsEntries = Object.entries(counts);
    if (pie && !countsEntries.some(entry => entry[1] > 0)) {
      countsEntries.push(['No Values in Range', 1]);
    }
    let nData = countsEntries.map(([name, value]) => {
      const zeroTotalPercent = value > 0 ? 100 : 0;
      return {
        name,
        value,
        percent: total > 0 ? value / total * 100 : zeroTotalPercent,
        total
      };
    });
    if (sortValues && INCOMPATIBLE_VALUE_TYPES.includes(valueAttr.valueType)) {
      nData.sort((a, b) => b.value - a.value);
    }
    if (limit) {
      nData = nData.slice(0, Math.min(limit, nData?.length));
    }
    if (donutCount) return donutCount === 'total' ? total : nData?.length;
    return nData;
  }, [data, attribute, valueRanges, includeNull, limit, pie, donutCount, isAgg, sortValues, query]);
}